import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "@phosphor-icons/react";
import { HERO_SLIDES } from "@/data/content";

export default function Hero() {
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => {
      setIdx((i) => (i + 1) % HERO_SLIDES.length);
    }, 6000);
    return () => clearInterval(t);
  }, []);

  const slide = HERO_SLIDES[idx];

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-[100vh] pt-24 md:pt-28 overflow-hidden bg-white"
    >
      {/* Background image fading in from the right */}
      <AnimatePresence mode="wait">
        <motion.div
          key={slide.image}
          initial={{ opacity: 0, scale: 1.05 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${slide.image})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
      </AnimatePresence>

      {/* White wash on the left → image on the right */}
      <div className="absolute inset-0 bg-gradient-to-r from-white via-white/90 to-white/30" />
      <div className="absolute inset-0 bg-gradient-to-t from-white/95 via-white/40 to-white/0" />
      <div className="absolute inset-0 grid-bg opacity-60" />

      {/* Decorative blue bar on the left */}
      <div className="absolute top-0 left-0 h-full w-[3px] bg-gradient-to-b from-[#1D4ED8] via-[#2563EB] to-transparent opacity-40" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-32 md:pb-40 grid md:grid-cols-12 gap-10 items-end min-h-[calc(100vh-6rem)]">
        <div className="md:col-span-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.6 }}
            >
              <div className="overline mb-5">{slide.eyebrow}</div>
              <h1
                data-testid="hero-title"
                className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[1.02] font-black text-slate-900 max-w-4xl"
              >
                {slide.title}
              </h1>
              <p className="mt-6 text-base md:text-lg text-slate-600 max-w-2xl leading-relaxed font-light">
                {slide.subtitle}
              </p>
            </motion.div>
          </AnimatePresence>

          <div className="mt-10 flex flex-wrap gap-3">
            <Link
              to="/contact"
              data-testid="hero-primary-cta"
              className="btn-primary"
            >
              Start a Project <ArrowUpRight size={16} weight="bold" />
            </Link>
            <Link
              to="/services"
              data-testid="hero-secondary-cta"
              className="btn-ghost"
            >
              Explore Services <ArrowRight size={16} weight="bold" />
            </Link>
          </div>
        </div>

        <div className="md:col-span-4 hidden md:flex flex-col gap-3 self-end">
          {HERO_SLIDES.map((s, i) => (
            <button
              key={i}
              data-testid={`hero-pill-${i}`}
              onClick={() => setIdx(i)}
              className={`text-left px-5 py-4 border transition-colors backdrop-blur-md ${
                i === idx
                  ? "border-[#1D4ED8] bg-[#DBEAFE]/70"
                  : "border-slate-300 bg-white/70 hover:border-slate-500"
              }`}
            >
              <div className={`text-[10px] tracking-[0.22em] uppercase font-mono ${i === idx ? "text-[#1D4ED8]" : "text-slate-500"}`}>
                {`0${i + 1} / 0${HERO_SLIDES.length}`}
              </div>
              <div className={`mt-1 font-display text-sm leading-snug ${i === idx ? "text-slate-900" : "text-slate-700"}`}>
                {s.eyebrow}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Bottom corner mark */}
      <div className="absolute bottom-6 right-6 md:right-12 font-mono text-[10px] tracking-[0.3em] uppercase text-slate-500">
        {`Slide 0${idx + 1} / 0${HERO_SLIDES.length}`}
      </div>
    </section>
  );
}
